import { ref as storageRef, getBytes } from 'firebase/storage';
import { auth, storage } from '../firebaseConfig';

const isHttpUrl = (value = '') => /^https?:\/\//i.test(value);
const isGsUrl = (value = '') => /^gs:\/\//i.test(value);
const normalizeProxyBaseUrl = (value = '') => String(value || '').trim().replace(/\/$/, '');

const isLocalDevHost = () => {
  if (typeof window === 'undefined') return false;
  const host = String(window.location.hostname || '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]';
};

const extractStoragePath = (value = '') => {
  try {
    if (!value || !isHttpUrl(value)) return '';
    const parsed = new URL(value);
    const marker = '/o/';
    const markerIndex = parsed.pathname.indexOf(marker);
    if (markerIndex < 0) return '';
    const encodedPath = parsed.pathname.slice(markerIndex + marker.length);
    return decodeURIComponent(encodedPath || '');
  } catch {
    return '';
  }
};

const buildStorageRefCandidates = (storageUrl) => {
  const candidates = [];
  const seen = new Set();

  const pushCandidate = (ref) => {
    if (!ref) return;
    const key = `${ref.bucket}/${ref.fullPath}`;
    if (seen.has(key)) return;
    seen.add(key);
    candidates.push(ref);
  };

  if (isHttpUrl(storageUrl) || isGsUrl(storageUrl)) {
    try {
      pushCandidate(storageRef(storage, storageUrl));
    } catch {
      // URL pode nao estar no formato esperado pelo SDK; segue para outros candidatos.
    }
  }

  const extractedPath = extractStoragePath(storageUrl);
  if (extractedPath) {
    try {
      pushCandidate(storageRef(storage, extractedPath));
    } catch {
      // ignora candidato invalido.
    }
  }

  return { candidates, extractedPath };
};

const buildProxyCandidates = () => {
  const projectId = storage?.app?.options?.projectId || '';
  const configuredProxy = normalizeProxyBaseUrl(process.env.REACT_APP_PDF_PROXY_URL);
  const cloudFunctionProxy = projectId
    ? `https://us-central1-${projectId}.cloudfunctions.net/proxyPdf`
    : '';
  const localDev = isLocalDevHost();

  const candidates = localDev
    ? [configuredProxy, cloudFunctionProxy, '/api/pdf-proxy']
    : [configuredProxy, '/api/pdf-proxy', cloudFunctionProxy];

  return [...new Set(candidates.filter(Boolean))];
};

const fetchViaProxy = async (storageUrl) => {
  const token = await auth.currentUser?.getIdToken?.();
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

  let lastError = null;
  const endpoints = buildProxyCandidates();

  for (const baseUrl of endpoints) {
    try {
      const separator = baseUrl.includes('?') ? '&' : '?';
      const proxyUrl = `${baseUrl}${separator}url=${encodeURIComponent(storageUrl)}`;
      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const error = new Error(`Proxy PDF falhou (${response.status}).`);
        error.status = response.status;
        throw error;
      }

      const contentType = String(response.headers.get('content-type') || '').toLowerCase();
      if (!contentType.includes('application/pdf')) {
        throw new Error('Resposta invalida do proxy de PDF.');
      }

      const buffer = await response.arrayBuffer();
      return new Uint8Array(buffer);
    } catch (error) {
      lastError = error;
      if (error?.status === 401) {
        throw new Error('Sua sessao expirou. Faca login novamente para abrir o PDF.');
      }
    }
  }

  throw lastError || new Error('Falha ao carregar PDF pelo proxy interno.');
};

const toFriendlyDownloadError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  const code = String(error?.code || '');

  if (code.includes('storage/unauthorized')) {
    return new Error('Sem permissao para acessar este PDF. Verifique sua assinatura/login.');
  }
  if (code.includes('storage/object-not-found')) {
    return new Error('Arquivo PDF nao encontrado no Storage.');
  }
  if (code.includes('storage/retry-limit-exceeded')) {
    return new Error('Tempo limite ao baixar o PDF. Tente novamente.');
  }
  if (error?.status === 404) {
    return new Error('Endpoint de proxy PDF nao encontrado. Publique Hosting + Functions.');
  }
  if (error?.status === 500) {
    return new Error('Falha interna no proxy PDF. Tente novamente em instantes.');
  }
  if (message.includes('resposta invalida do proxy')) {
    return new Error('Proxy PDF respondeu invalido. No localhost, configure REACT_APP_PDF_PROXY_URL.');
  }
  if (error instanceof TypeError || message.includes('failed to fetch')) {
    return new Error('Nao foi possivel conectar ao proxy PDF. Verifique o deploy da function.');
  }

  return error || new Error('Falha ao baixar o PDF.');
};

export async function downloadPdfData(storageUrl) {
  if (!storageUrl) throw new Error('URL do PDF não encontrada.');

  const allowStorageFallback =
    !isLocalDevHost() &&
    String(process.env.REACT_APP_PDF_ALLOW_STORAGE_FALLBACK || '').toLowerCase() === 'true';

  let lastError = null;

  try {
    return await fetchViaProxy(storageUrl);
  } catch (error) {
    lastError = error;
  }

  if (!allowStorageFallback) {
    throw toFriendlyDownloadError(lastError);
  }

  const { candidates } = buildStorageRefCandidates(storageUrl);
  for (const ref of candidates) {
    try {
      const bytes = await getBytes(ref);
      return new Uint8Array(bytes);
    } catch (error) {
      lastError = error;
    }
  }

  throw toFriendlyDownloadError(lastError);
}
